import { useForm } from '@tanstack/react-form'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { toast } from 'sonner'
import { Minus, Plus } from 'lucide-react'

import type { InquiryEvent } from '@/gen/cortado/v1/inquiries_pb'
import { useUpdateInquiryEvent } from '@/stores/leads.store'
import { useResolveLocationTimezone } from '@/stores/events.store'
import { dateToTimestamp } from '@/lib/date-utils'
import { formatTimestampToTimeString, combineDateAndTime } from '@/lib/inquiry-utils'
import { useEventTypes } from '@/hooks/use-event-types'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { DurationInput } from '@/components/shared/duration-input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PlacesAutocomplete } from '@/components/ui/places-autocomplete'
import { InputGroup } from '@/components/ui/input-group'
import { DatePicker } from '@/components/shared/date-picker'
import { TimePicker } from '@/components/shared/time-picker'
import { BaristaCartCountFields } from '@/components/shared/barista-cart-count-fields'
import { TimezoneSelect } from '@/components/shared/timezone-select'
import { LocationTimezoneSync } from '@/components/shared/location-timezone-sync'

interface InquiryEventEditSheetProps {
  inquiryId: string
  event: InquiryEvent
  eventIndex: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * InquiryEventEditSheet - Sheet component for editing individual inquiry events
 *
 * Features:
 * - Edit event date, duration, and guest count
 * - Uses TanStack Form for form state management
 * - Leverages useUpdateInquiryEvent hook for cache updates
 * - Displays loading states during mutation
 * - Shows toast notifications on success/error
 * - Resets form on close
 *
 * @param inquiryId - The ID of the parent inquiry
 * @param event - The event to edit
 * @param eventIndex - The index of the event (for display as Event #)
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function InquiryEventEditSheet({
  inquiryId,
  event,
  eventIndex,
  open,
  onOpenChange,
}: InquiryEventEditSheetProps) {
  const { updateEvent, isLoading } = useUpdateInquiryEvent(inquiryId)
  const { resolve: resolveTimezoneFromLocation } = useResolveLocationTimezone()

  // Convert proto Timestamp to Date if available
  const getInitialDate = (): Date | undefined => {
    if (!event.eventDate) return undefined
    const seconds = typeof event.eventDate.seconds === 'bigint'
      ? Number(event.eventDate.seconds)
      : event.eventDate.seconds
    return new Date(seconds * 1000)
  }

  // Get initial time from event date, default to 12:00 if not set
  const getInitialTime = (): string => {
    return formatTimestampToTimeString(event.eventDate) || '12:00'
  }

  // Archived types are not assignable to new records, but the one already
  // on this record stays selectable so the field isn't blank.
  const { getEventTypeOptions } = useEventTypes()

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      eventDate: getInitialDate(),
      eventTime: getInitialTime(),
      eventLength: event.eventLength ?? 0,
      eventGuests: event.eventGuests ?? 0,
      baristaCount: event.baristaCount ?? 1,
      cartCount: event.cartCount ?? 1,
      eventType: event.eventTypeKey ?? '',
      eventLocation: event.eventLocation ?? '',
      eventTimezone: event.eventTimezone ?? '',
    },
    onSubmit: async ({ value }) => {
      try {
        // Build update request with field mask
        const updateMask: string[] = []
        const params: {
          id: string
          eventDate?: ReturnType<typeof dateToTimestamp>
          eventLength?: number
          eventGuests?: number
          baristaCount?: number
          cartCount?: number
          eventType?: string
          eventLocation?: string
          eventTimezone?: string
          updateMask?: string[]
        } = {
          id: event.id,
        }

        // Only include changed fields
        if (value.eventDate) {
          const combinedDate = combineDateAndTime(value.eventDate, value.eventTime)
          params.eventDate = dateToTimestamp(combinedDate)
          updateMask.push('event_date')
        }

        if (value.eventLength !== undefined) {
          params.eventLength = value.eventLength || undefined
          updateMask.push('event_length')
        }

        if (value.eventGuests !== undefined) {
          params.eventGuests = value.eventGuests
          updateMask.push('event_guests')
        }

        if (value.baristaCount !== undefined) {
          params.baristaCount = value.baristaCount
          updateMask.push('barista_count')
        }

        if (value.cartCount !== undefined) {
          params.cartCount = value.cartCount
          updateMask.push('cart_count')
        }

        if (value.eventType !== undefined) {
			params.eventType = value.eventType || undefined
          updateMask.push('event_type')
        }

        if (value.eventLocation !== undefined) {
          params.eventLocation = value.eventLocation.trim() || undefined
          updateMask.push('event_location')
        }

        params.eventTimezone = value.eventTimezone || undefined
        updateMask.push('event_timezone')

        params.updateMask = updateMask

        await updateEvent(params)

        toast.success('Event updated successfully')
        onOpenChange(false)
        form.reset()
      } catch (error) {
        console.error('Failed to update event:', error)
        reportError(error, { feature: 'inquiry_event_update', kind: 'mutation' })
        toast.error('Failed to update event', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Edit Event #{eventIndex + 1}</SheetTitle>
          <SheetDescription>
            Update the event details. Click save when you&apos;re done.
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="inquiry-event-edit-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
            <div className="grid gap-3">
              <Label>Event Date & Time</Label>
              <div className="grid grid-cols-2 gap-2">
                <form.Field name="eventDate">
                  {(field) => (
                    <DatePicker
                      value={field.state.value}
                      onChange={(date) => field.handleChange(date)}
                      disabled={isLoading}
                    />
                  )}
                </form.Field>
                <form.Field name="eventTime">
                  {(field) => (
                    <TimePicker
                      value={field.state.value}
                      onChange={(time) => field.handleChange(time)}
                      disabled={isLoading}
                    />
                  )}
                </form.Field>
              </div>
            </div>

            <form.Subscribe selector={(s) => s.values.eventLocation}>
              {(eventLocation) => (
                <form.Field name="eventTimezone">
                  {(field) => {
                    const trimmedLocation = (eventLocation ?? '').trim()
                    return (
                      <div className="grid gap-1">
                        <TimezoneSelect
                          id="event-timezone"
                          label="Timezone"
                          value={field.state.value || undefined}
                          onChange={(value) => field.handleChange(value)}
                          disabled={isLoading}
                          onResolveFromLocation={() => resolveTimezoneFromLocation(trimmedLocation)}
                          resolveDisabledReason={trimmedLocation ? undefined : 'Set an event location first'}
                        />
                        <p className="text-xs text-muted-foreground">
                          Follows the event location automatically. Pick a zone here to override it.
                        </p>
                      </div>
                    )
                  }}
                </form.Field>
              )}
            </form.Subscribe>

            <form.Field name="eventLength">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-length">Duration</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <DurationInput
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      disabled={isLoading}
                      min={0}
                      step={0.25}
                    />
                  </div>
                </div>
              )}
            </form.Field>

            <form.Field name="eventGuests">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-guests">Guest Count</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-r border-input"
                      onClick={() => {
                        const newValue = Math.max(0, field.state.value - 25)
                        field.handleChange(newValue)
                      }}
                      disabled={isLoading || field.state.value <= 0}
                    >
                      <Minus className="h-4 w-4" />
                      <span className="sr-only">Decrease by 25</span>
                    </Button>
                    <Input
                      id="event-guests"
                      type="number"
                      min="0"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const value = Number(e.target.value) || 0
                        const clampedValue = Math.max(0, value)
                        field.handleChange(clampedValue)
                        field.handleBlur()
                      }}
                      disabled={isLoading}
                      placeholder="0"
                      className="border-0 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-l border-input"
                      onClick={() => {
                        const newValue = field.state.value + 25
                        field.handleChange(newValue)
                      }}
                      disabled={isLoading}
                    >
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Increase by 25</span>
                    </Button>
                  </div>
                </div>
              )}
            </form.Field>

            <form.Field name="baristaCount">
              {(baristaField) => (
                <form.Field name="cartCount">
                  {(cartField) => (
                    <BaristaCartCountFields
                      baristaCount={baristaField.state.value}
                      cartCount={cartField.state.value}
                      onBaristaCountChange={baristaField.handleChange}
                      onCartCountChange={cartField.handleChange}
                      onBaristaCountBlur={baristaField.handleBlur}
                      onCartCountBlur={cartField.handleBlur}
                      disabled={isLoading}
                    />
                  )}
                </form.Field>
              )}
            </form.Field>

            <form.Field name="eventType">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-type">Event Type</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    disabled={isLoading}
                  >
                    <SelectTrigger id="event-type" className="w-full">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {getEventTypeOptions(field.state.value).map((eventType) => (
                        <SelectItem key={eventType.key} value={eventType.key}>
                          {eventType.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="eventLocation">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-location">Location</Label>
                  <InputGroup>
                    <PlacesAutocomplete
                      id="event-location"
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      onBlur={field.handleBlur}
                      placeholder="Enter event location"
                      disabled={isLoading}
                    />
                  </InputGroup>
                  <form.Subscribe selector={(s) => s.values.eventTimezone}>
                    {(eventTimezone) => (
                      <LocationTimezoneSync
                        location={field.state.value ?? ''}
                        timezone={eventTimezone ?? ''}
                        onTimezoneChange={(timezone) => form.setFieldValue('eventTimezone', timezone)}
                        resolve={resolveTimezoneFromLocation}
                      />
                    )}
                  </form.Subscribe>
                </div>
              )}
            </form.Field>
          </form>
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </SheetClose>
          <Button type="submit" form="inquiry-event-edit-form" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
